<template>
    <!-- novalidate: the application validates on its own and shows the result in the field,
         instead of the bubble of the browser, which ignores the language and the style of the app -->
    <form ref="formElement" class="app-form" novalidate @submit="handleSubmit" @reset="handleReset">
        <slot />
    </form>
</template>

<script lang="ts">
import { defineComponent, getCurrentInstance } from "vue";
import { provideFieldValidationRegistry } from "@/utils/form-validation";

export default defineComponent({
    name: "AppForm",

    emits: ["submit", "reset", "invalid", "edited"],

    setup() {
        const instance = getCurrentInstance();

        return {
            // Fields register their explicit validators and any built-in constraints,
            // such as the bounds and step of a numeric TextField.
            fieldValidators: provideFieldValidationRegistry(() => {
                // A field was edited, so whatever the form was saying about the previous
                // attempt is stale. Bind @edited to clear the FormError next to the actions
                instance?.proxy?.$emit("edited");
            }),
        };
    },

    methods: {
        /**
         * Checks every field of the form and reports whether it can be sent. Public so a
         * parent can call it through a ref when the button that submits lives outside the
         * form, as the steps of the data space configuration wizard do.
         */
        validate: function (): boolean {
            // Every field is checked, not only up to the first failure, so the user sees
            // all the problems at once instead of one per attempt
            let failures = 0;

            for (const validateField of this.fieldValidators) {
                if (validateField()) {
                    failures++;
                }
            }

            if (failures > 0) {
                this.focusFirstInvalidField();

                // The count travels with the event: a single field speaks for itself,
                // while several failing at once also deserve a summary next to the actions
                this.$emit("invalid", failures);
                return false;
            }

            return true;
        },

        /**
         * Takes the user to the first field that failed. On a long form the field that holds
         * the submit back is often below the fold, and without this nothing seems to happen.
         */
        focusFirstInvalidField: function () {
            this.$nextTick(() => {
                // not $el: the comment above the form makes this component a fragment,
                // so its first node is the comment and not the form
                const form = this.$refs.formElement as HTMLElement | undefined;
                const invalid = form?.querySelector<HTMLElement>('[aria-invalid="true"]');

                if (!invalid) {
                    return;
                }

                const control = invalid.matches('[role="radiogroup"]')
                    ? invalid.querySelector<HTMLElement>('input[type="radio"]:not(:disabled)')
                    : invalid;
                control?.focus({ preventScroll: true });
                invalid.scrollIntoView({ block: "center", behavior: "smooth" });
            });
        },

        handleSubmit: function (event: Event) {
            event.preventDefault();

            if (!this.validate()) {
                return;
            }

            this.$emit("submit");
        },

        handleReset: function (event: Event) {
            event.preventDefault();
            this.$emit("reset");
        },
    },
});
</script>

<style scoped>
@import "@/style/components/forms/forms.css";
</style>
